import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useDoils, usePatchYearSettings } from "../api/hooks";
import type { DoilsPage as Data } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { LoadError, Loading, useYear } from "../components/Layout";
import { dlong, dshort, num, toNum } from "../lib/format";

export function DoilsPage() {
  const year = useYear();
  const q = useDoils(year);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return <DoilsView key={year} data={q.data} />;
}

function DoilsView({ data }: { data: Data }) {
  const { year, summary: b, doil, rules } = data;
  const [sold, setSold] = useState(num(data.year_settings.doils_sold));
  const patch = usePatchYearSettings(year);
  const flash = useFlash();
  const last = doil.fortnights[doil.fortnights.length - 1];

  const saveSold = (e: FormEvent) => {
    e.preventDefault();
    patch.mutate(
      { doils_sold: toNum(sold) },
      { onSuccess: () => flash(`${year} settings saved.`), onError: (err) => flash(errorText(err), "error") },
    );
  };

  return (
    <>
      <div className="page-head">
        <h1>DOILs, {year}</h1>
      </div>

      <div className="two-col">
        <div className="panel">
          <h2>Balance</h2>
          <dl className="rows">
            <dt>Carried in from {year - 1}</dt>
            <dd>{num(b.doil_start)}</dd>
            <dt>From fortnights over {rules.doil_limit} days</dt>
            <dd>{num(doil.from_fortnights)}</dd>
            <dt>From bank holidays worked</dt>
            <dd>{num(doil.from_bh)}</dd>
            <dt>Used (marked on the planner)</dt>
            <dd>{num(b.doil_used)}</dd>
            <dt>Sold</dt>
            <dd>{num(b.doil_sold)}</dd>
            <dt className="total">Balance at year end</dt>
            <dd className="total">{num(b.doil_end)}</dd>
            <dt>Balance today (finished fortnights only)</dt>
            <dd>{num(b.doil_now)}</dd>
          </dl>
          <form onSubmit={saveSold} className="actions">
            <div style={{ maxWidth: "10rem" }}>
              <label htmlFor="sold">DOILs sold in {year}</label>
              <input
                id="sold"
                type="number"
                step="0.5"
                min="0"
                value={sold}
                onChange={(e) => setSold(e.target.value)}
              />
            </div>
            <button
              className="btn quiet"
              type="submit"
              style={{ alignSelf: "end" }}
              disabled={patch.isPending}
            >
              Save
            </button>
          </form>
        </div>

        <div className="panel">
          <h2>How DOILs are earned</h2>
          <p>
            Worked days are job days plus base days. Fortnights run every 14 days from{" "}
            {dlong(rules.fortnight_anchor)}, and each fortnight counts in the year it finishes in.
          </p>
          <p>
            {rules.doil_mode === "one"
              ? `One DOIL for any fortnight with more than ${rules.doil_limit} worked days.`
              : `One DOIL for each worked day over ${rules.doil_limit} in a fortnight.`}{" "}
            Every bank holiday worked earns one more
            {rules.bh_counts_toward_limit && `, and also counts toward the ${rules.doil_limit} days`}.
          </p>
          <p className="small">
            <Link to={`/year/${year}/settings`}>Change these rules</Link>
          </p>
        </div>
      </div>

      <div className="panel" style={{ marginTop: "1.25rem" }}>
        <h2>Fortnights finishing in {year}</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>From</th>
                <th>To</th>
                <th className="num">Worked</th>
                <th className="num">Bank hols worked</th>
                <th className="num">Counted</th>
                <th className="num">DOILs</th>
                <th className="num" title="Your DOIL balance at the end of the fortnight">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {doil.fortnights.map((f) => (
                <tr key={f.n} className={`is-${f.state}`}>
                  <td>{f.n}</td>
                  <td>
                    {dshort(f.start)}
                    {f.spans_years && ` ${f.start.slice(0, 4)}`}
                  </td>
                  <td>{dshort(f.end)}</td>
                  <td className="num">
                    {num(f.worked)}
                    {f.carried > 0 && <span className="small muted"> ({num(f.carried)} from setup)</span>}
                  </td>
                  <td className="num">{num(f.bh_worked)}</td>
                  <td className="num">{num(f.counted)}</td>
                  <td className="num">
                    {f.earned ? <span className="pill gain">+{num(f.earned)}</span> : 0}
                  </td>
                  <td className="num">
                    <b>{num(f.balance)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6}>From fortnights</td>
                <td className="num">{num(doil.from_fortnights)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        {last && !last.end.endsWith("-12-31") && (
          <p className="small muted" style={{ marginTop: ".6rem" }}>
            The fortnight running over New Year counts in {year + 1}.
          </p>
        )}
      </div>

      <div className="two-col" style={{ marginTop: "1.25rem" }}>
        <div className="panel">
          <h2>Bank holidays {year}</h2>
          <div className="table-wrap">
            <table>
              <tbody>
                {doil.bank_holidays.map((bh) => (
                  <tr key={bh.date}>
                    <td>{dshort(bh.date)}</td>
                    <td>{bh.name}</td>
                    <td className="num">{bh.worked && <span className="pill gain">Worked +1</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small" style={{ marginTop: ".6rem" }}>
            <Link to={`/year/${year}/settings#bank-holidays`}>Add a one-off bank holiday</Link>
          </p>
        </div>

        <div className="panel">
          <h2>History</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Year</th>
                  <th className="num">Start</th>
                  <th className="num">Earned</th>
                  <th className="num">Used</th>
                  <th className="num">Sold</th>
                  <th className="num">End</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.year} className={h.year === year ? "is-current" : undefined}>
                    <td>
                      {h.year}
                      {h.legacy && <span className="small muted"> from spreadsheet</span>}
                    </td>
                    <td className="num">{num(h.start)}</td>
                    <td className="num">{num(h.accrue)}</td>
                    <td className="num">{num(h.used)}</td>
                    <td className="num">{num(h.sold)}</td>
                    <td className="num">
                      <strong>{num(h.end_bal)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
